
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lead } from "@/types";
import { ArrowLeft, Phone, Calendar, User, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import CallResultsDisplay from "@/components/calls/CallResultsDisplay";
import { LeadInfoCard } from "@/components/calls/LeadInfoCard";
import { NotFoundCard } from "@/components/calls/NotFoundCard";

const CallDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    if (!id) return;
    
    const fetchCallAndLead = async () => {
      setIsLoading(true);
      
      try {
        // Fetch the call details to get the lead ID
        const { data: callData, error: callError } = await supabase
          .from("calls")
          .select("lead_id")
          .eq("id", id)
          .single();
        
        if (callError) throw callError;
        
        if (callData && callData.lead_id) {
          // Fetch the lead details
          const { data: leadData, error: leadError } = await supabase
            .from("leads")
            .select("*")
            .eq("id", callData.lead_id)
            .single();
          
          if (leadError) throw leadError;
          
          setLead(leadData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCallAndLead();
  }, [id]);
  
  const goBack = () => navigate(-1);
  
  return (
    <div className="container mx-auto max-w-3xl py-6">
      <Button variant="outline" className="mb-6" onClick={goBack}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>
      
      <div className="grid gap-6">
        {/* Lead Info Card or Not Found State */}
        {renderLeadSection(id, lead, isLoading)}
        
        {/* Call Results Display Component */}
        {id && <CallResultsDisplay callId={id} />}
      </div>
    </div>
  );
};

// Helper function to render the lead section based on state
const renderLeadSection = (id: string | undefined, lead: Lead | null, isLoading: boolean) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin w-6 h-6 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4">Loading lead information...</p>
        </CardContent>
      </Card>
    );
  }
  
  if (!id) {
    return <NotFoundCard message="Call ID Not Found" description="The call ID was not provided in the URL." />;
  }
  
  if (lead) {
    return <LeadInfoCard lead={lead} />;
  }
  
  return <NotFoundCard message="Lead Not Found" description="The lead associated with this call could not be found." />;
};

export default CallDetails;
